import { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import type { IconType } from "react-icons";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { ActionIcon, Dropdown } from "rizzui";
import { cn } from "@steelyard/theme";

/**
 * A list row's actions: ONE `⋮` button at the start of the row, opening a menu with every action
 * the row has — the Marketplace admin portal's `RowActions` (2026-09-28, the project owner's
 * reference): the always-present one (View / Edit) first, then the rest, destructive items last in
 * red under a divider. The column leads the row (`actionsFirst`, ./table-view.tsx) and is only as
 * wide as the button.
 *
 * The menu is COPIED from vendor/isomorphic/apps/isomorphic/src/app/shared/support/inbox/action-dropdown.tsx
 * (rizzui `Dropdown`, an `ActionIcon` trigger, icon + label items). Nothing in this app hard-deletes,
 * so there is no trash action to offer.
 *
 * Every item is a route the app already has. A caller passes `false` for an item its permission or
 * the row's state rules out, so the gating stays in the column file next to the data it reads, and
 * the `⋮` disappears when nothing is left. rizzui's `Dropdown.Item` renders a button, never a link,
 * so an item navigates on select — Cancel… and Reject… land on the detail page's own `?act=`
 * ReasonDialog, where its header buttons go too, so a reason is still required before anything
 * commits.
 *
 * **A click on the row opens its primary item too** (2026-09-29, the project owner's request): the
 * row a `RowActions` sits in provides a `RowPrimaryContext` slot (./table-view.tsx), and this
 * component fills it with its `primary` action, so every list gets row clicks with no per-screen
 * wiring and the row opens exactly what the menu's first item opens — never a second copy of the
 * route. Only `primary` is registered: a destructive or secondary item is never one click away.
 */

/** What a row click runs: the row's primary action, and its route for a Ctrl/Cmd-click new tab. */
export type RowPrimary = { run: () => void; href?: string };
export const RowPrimaryContext = createContext<{ current: RowPrimary | null } | null>(null);
/**
 * A menu item: a route (`to`), or an in-page act (`onClick` — an edit form on the same screen, a
 * confirmation), never both.
 */
export type RowAction = { label: string; icon: IconType; newTab?: boolean; danger?: boolean } & (
  | { to: string; onClick?: never }
  | { onClick: () => void; to?: never }
);

export function RowActions({
  primary,
  more = [],
  name,
}: {
  /** The action every row has (View / Edit): the menu's first item. */
  primary?: RowAction;
  more?: (RowAction | false | null | undefined)[];
  /** The row's own name, for the button's accessible label ("Actions for SO-1001"). */
  name: string;
}) {
  const navigate = useNavigate();
  const rowSlot = useContext(RowPrimaryContext);
  const available = [primary, ...more].filter((item): item is RowAction => Boolean(item));
  const items = [...available.filter((item) => !item.danger), ...available.filter((item) => item.danger)];
  const firstDanger = items.findIndex((item) => item.danger);

  if (items.length === 0) {
    if (rowSlot) rowSlot.current = null;
    return null;
  }

  const open = (item: RowAction) => {
    if (item.onClick) item.onClick();
    else if (item.newTab) window.open(item.to, "_blank", "noopener");
    else navigate(item.to!);
  };

  // Registered during render so the row has it by the time anyone can click; a row is rendered
  // afresh with its cells, so the slot never outlives the action it names.
  if (rowSlot) rowSlot.current = primary ? { run: () => open(primary), href: primary.to } : null;

  return (
    <Dropdown placement="bottom-start">
      <Dropdown.Trigger aria-label={`Actions for ${name}`} title="Actions">
        <ActionIcon
          as="span"
          size="sm"
          variant="text"
          data-row-primary={primary ? "" : undefined}
          className="text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        >
          <PiDotsThreeVerticalBold className="size-4" />
        </ActionIcon>
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-56">
        {items.map((item, index) => (
          <Dropdown.Item
            key={item.label}
            onClick={() => open(item)}
            className={cn(
              "gap-2.5 text-sm text-gray-900",
              item.danger && "text-red-dark",
              index === firstDanger && index > 0 && "mt-1 border-t border-muted pt-2",
            )}
          >
            <item.icon aria-hidden className="size-4 shrink-0" />
            {item.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}
